import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/auth/AuthLayout';
import FormInput from '../components/auth/FormInput';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const { forgotPassword, error, clearError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    clearError();

    if (!email) {
      setFormError(t('auth.forgotPassword.errors.enterEmail'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError(t('auth.forgotPassword.errors.invalidEmail'));
      return;
    }

    setIsSubmitting(true);
    try {
      await forgotPassword(email);
      setIsSuccess(true);
    } catch (err) {
      // Don't reveal if email exists or not (security)
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <AuthLayout tagline={t('auth.taglines.forgotPassword')}>
        <div className="auth-form-header">
          <h1 className="auth-form-title">{t('auth.forgotPassword.successTitle')}</h1>
          <p className="auth-form-subtitle">{t('auth.forgotPassword.successSubtitle')}</p>
        </div>

        <div className="success-message">
          <div className="success-icon">
            <svg viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
            </svg>
          </div>
          <p className="success-text">
            <Trans
              i18nKey="auth.forgotPassword.successMessage"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          </p>
          <p className="success-hint">
            {t('auth.forgotPassword.successHint')}
          </p>
        </div>

        <Link to="/login" className="btn btn-primary">
          {t('auth.login.submit')}
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout tagline={t('auth.taglines.forgotPassword')}>
      <div className="auth-form-header">
        <h1 className="auth-form-title">{t('auth.forgotPassword.title')}</h1>
        <p className="auth-form-subtitle">
          {t('auth.forgotPassword.subtitle')}
        </p>
      </div>

      {(formError || error) && (
        <div className="form-alert form-alert-error">
          <svg viewBox="0 0 24 24" className="form-alert-icon">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
          </svg>
          <span>{formError || error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <FormInput
          label={t('auth.forgotPassword.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.forgotPassword.emailPlaceholder')}
          icon="email"
          required
          autoComplete="email"
        />

        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="btn-spinner" />
              {t('auth.forgotPassword.submitting')}
            </>
          ) : (
            t('auth.forgotPassword.submit')
          )}
        </button>
      </form>

      <div className="auth-footer">
        <Link to="/login">{t('auth.forgotPassword.backToLogin')}</Link>
      </div>
    </AuthLayout>
  );
}
